import { Link, useLocation } from 'wouter';
import { useAuth } from '../context/AuthContext';
import { useState } from 'react';
import { CommandPalette } from './CommandPalette';
import { can } from '../lib/permissions';
import { MacquarieUploadDialog } from './MacquarieUploadDialog';

export function Shell({ children }: { children: React.ReactNode }) {
  const [uploadOpen, setUploadOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { user, signOut } = useAuth();
  const [path] = useLocation();

  const nav = [
    { to: '/', label: 'Dashboard', perm: 'client.view' as const },
    { to: '/today', label: 'Today', perm: 'client.view' as const },
    { to: '/pipeline', label: 'Pipeline', perm: 'client.view' as const },
    { to: '/clients/new', label: 'New Client', perm: 'client.edit' as const },
    { to: '/activity', label: 'Activity', perm: 'client.view' as const },
    { to: '/reports', label: 'Reports', perm: 'audit.read' as const, alt: 'export.run' as const },
    { to: '/audit', label: 'Audit', perm: 'audit.read' as const },
    { to: '/users', label: 'Users', perm: 'users.manage' as const },
    { to: '/admin/match-candidates', label: 'Duplicates', perm: 'users.manage' as const },
    { to: '/admin/unlinked-file-notes', label: 'Unlinked notes', perm: 'users.manage' as const },
    { to: '/admin/feed-reconciliation', label: 'Feed', perm: 'users.manage' as const },
  ];

  const links = nav.filter((n) => can(user?.role, n.perm) || (n.alt && can(user?.role, n.alt)));
  const isActive = (to: string) => path === to || (to !== '/' && path.startsWith(to));

  return (
    <div className="min-h-screen flex flex-col">
      <header className="qa-page-head px-4 sm:px-6 lg:px-10 flex items-center justify-between gap-3 sm:gap-6">
        <div className="flex items-baseline gap-6">
          <Link to="/" className="flex items-baseline gap-2">
            <span className="qa-eyebrow">CLIENT—BASE</span>
            <span className="qa-display text-xl">cb<span className="qa-italic-accent">.ai</span></span>
          </Link>
          <nav className="hidden md:flex items-center gap-5 ml-4">
            {links.map(n => {
              const active = isActive(n.to);
              return (
                <Link key={n.to} to={n.to}
                  className={`text-sm font-medium ${active ? 'text-ink' : 'text-ink-mute hover:text-ink'} ${active ? 'border-b-2 border-rust pb-1' : ''}`}>
                  {n.label}
                </Link>
              );
            })}
          </nav>
        </div>
        <div className="flex items-center gap-3 text-sm">
          <button type="button" className="btn-ghost text-sm md:hidden whitespace-nowrap" aria-expanded={menuOpen} aria-controls="mobile-nav"
            onClick={() => setMenuOpen((o) => !o)}>{menuOpen ? 'Close' : 'Menu'}</button>
          <span className="qa-mono text-xs text-ink-mute hidden sm:inline">{user?.email}</span>
          <span className="hidden sm:inline"><span className="qa-badge qa-badge--brass">{user?.role}</span></span>
          {can(user?.role, 'client.edit') && (
            <button onClick={() => setUploadOpen(true)} className="btn-ghost text-sm hidden sm:inline-block">Upload Report</button>
          )}
          <button onClick={signOut} className="btn-ghost text-sm whitespace-nowrap">Sign out</button>
        </div>
      </header>
      {menuOpen && (
        <nav id="mobile-nav" aria-label="Main" className="md:hidden qa-hairline-b px-6 py-2 flex flex-col">
          {links.map((n) => (
            <Link key={n.to} to={n.to} onClick={() => setMenuOpen(false)}
              className={`py-2 text-sm font-medium ${isActive(n.to) ? 'text-rust' : 'text-ink'}`}>
              {n.label}
            </Link>
          ))}
        </nav>
      )}
      <main className="flex-1 px-6 lg:px-10 py-6">{children}</main>
      <CommandPalette />
      <MacquarieUploadDialog open={uploadOpen} onClose={() => setUploadOpen(false)} context="shell" />
      <footer className="qa-hairline-t py-3 px-6 lg:px-10 text-xs text-ink-mute flex justify-between">
        <span>© {new Date().getFullYear()} Client-Base.ai — synthetic dev data, do not use for live client work</span>
        <span className="qa-mono">v0.1.0</span>
      </footer>
    </div>
  );
}
